(() => {
  if (customElements.get('bfcm-banner')) return;

  customElements.define('bfcm-banner', class extends HTMLElement {
    connectedCallback() {
      this.events?.abort();
      this.events = new AbortController();
      const options = { signal: this.events.signal };
      this.config = JSON.parse(this.querySelector('[data-bfcm-config]').textContent);
      this.perks = this.querySelector('[data-perks-dialog]');
      this.nav = this.querySelector('[data-campaign-nav]');
      this.mobile = window.matchMedia('(max-width: 767px)');
      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.updateTimeline();
      this.perks?.addEventListener('submit', (event) => this.onPerksSubmit(event), { ...options, capture: true });
      this.perks?.querySelector('[data-membership-retry]')?.addEventListener('click', () => this.checkMembership(), options);
      this.onCartUpdate = ({ cart, source, productVariantId } = {}) => {
        if (!this.perks || !Array.isArray(cart?.items)) return;
        const containsPerks = cart.items.some((item) => String(item.product_id) === this.perks.dataset.perksProductId);
        if (containsPerks || Number.isFinite(cart.total_price)) {
          this.perks.dataset.perksInCart = String(containsPerks);
          this.renderMembership(this.perks.dataset.membership);
          const variantId = this.perks.querySelector('[name="id"]')?.value;
          if (containsPerks && source === 'product-form' && String(productVariantId) === variantId) {
            this.perks.hide?.();
          }
        }
      };
      this.unsubscribe = window.theme?.pubsub?.subscribe(theme.pubsub.PUB_SUB_EVENTS.cartUpdate, this.onCartUpdate);
      this.checkMembership();
      if (!this.nav) return;
      this.navClearance = document.createElement('div');
      this.navClearance.className = 'bfcm-banner__nav-clearance';
      this.navClearance.setAttribute('aria-hidden', 'true');
      (this.closest('.section-box') || this).before(this.navClearance);
      this.navHome = document.createElement('div');
      this.navHome.className = 'bfcm-banner__nav-placeholder';
      this.nav.before(this.navHome);
      document.body.append(this.nav);
      this.schedule = () => {
        if (this.frame) return;
        this.frame = requestAnimationFrame(() => {
          this.frame = null;
          this.updateNavigation();
          if (this.headerIsMoving()) this.schedule();
        });
      };
      this.nav.addEventListener('click', (event) => this.onNavigationClick(event), options);
      window.addEventListener('scroll', this.schedule, { ...options, passive: true });
      const cancelNavigation = () => {
        if (!this.scrollNavigation) return;
        this.cancelNavigation();
        this.schedule();
      };
      for (const type of ['wheel', 'touchstart', 'pointerdown']) {
        window.addEventListener(type, cancelNavigation, { ...options, passive: true });
      }
      window.addEventListener('keydown', (event) => {
        if (['ArrowUp', 'ArrowDown', 'PageUp', 'PageDown', 'Home', 'End', ' '].includes(event.key)) cancelNavigation();
      }, options);
      window.addEventListener('resize', this.schedule, options);
      this.mobile.addEventListener('change', this.schedule, options);
      document.addEventListener('shopify:section:load', this.schedule, options);
      document.addEventListener('visibilitychange', () => { this.updateTimeline(); this.schedule(); }, options);
      document.addEventListener('DOMContentLoaded', this.schedule, { ...options, once: true });
      this.resize = new ResizeObserver(this.schedule);
      this.resize.observe(this.navHome);
      this.resize.observe(this.nav);
      this.header = document.querySelector('.header-section');
      this.topbars = Array.from(document.querySelectorAll('.topbar-section'));
      this.headerObserver = new MutationObserver(() => {
        this.holdHeaderState();
        this.schedule();
      });
      for (const element of [this.header, ...this.topbars].filter(Boolean)) {
        this.headerObserver.observe(element, { attributes: true, subtree: true, attributeFilter: ['class', 'style'] });
        this.resize.observe(element);
        for (const type of ['transitionrun', 'transitionend', 'transitioncancel']) {
          element.addEventListener(type, this.schedule, options);
        }
      }
      this.schedule();
    }

    disconnectedCallback() {
      this.events?.abort();
      this.cancelNavigation();
      this.membershipRequest?.abort();
      this.unsubscribe?.();
      this.resize?.disconnect();
      this.headerObserver?.disconnect();
      cancelAnimationFrame(this.frame);
      this.frame = null;
      this.activeLink = null;
      if (this.navHome && this.nav) this.navHome.replaceWith(this.nav);
      this.navClearance?.remove();
      if (this.perks?.parentElement === document.body) {
        this.perks.hide?.();
        this.append(this.perks);
      }
      document.documentElement.style.removeProperty('--bfcm-campaign-offset');
    }

    updateTimeline() {
      const start = Date.parse(this.config.start);
      const end = Date.parse(this.config.end);
      const timelines = this.querySelectorAll('[data-campaign-timeline]');
      try {
        if (!Number.isFinite(start) || !Number.isFinite(end) || end <= start) throw new Error('Invalid campaign dates');
        const formatter = new Intl.DateTimeFormat('en-US', { timeZone: this.config.timeZone, month: '2-digit', day: '2-digit' });
        const dateLabel = (date) => {
          const parts = formatter.formatToParts(new Date(date));
          return `${parts.find((part) => part.type === 'month').value}.${parts.find((part) => part.type === 'day').value}`;
        };
        const progress = 50;
        const label = `${dateLabel(start)} – ${dateLabel(end)}`;
        timelines.forEach((timeline) => {
          timeline.hidden = false;
          timeline.style.setProperty('--campaign-progress', `${progress}%`);
          for (const [key, value] of [['start', start], ['end', end]]) {
            const time = timeline.querySelector(`[data-campaign-${key}]`);
            time.dateTime = new Date(value).toISOString();
            time.textContent = dateLabel(value);
          }
          const track = timeline.querySelector('[role="progressbar"]');
          track.setAttribute('aria-valuenow', String(Math.round(progress)));
          track.setAttribute('aria-valuetext', label);
        });
      } catch {
        timelines.forEach((timeline) => { timeline.hidden = true; });
      }
    }

    async onPerksSubmit(event) {
      if (this.submitPerksForm) {
        this.submitPerksForm = false;
        return;
      }
      event.preventDefault();
      event.stopImmediatePropagation();
      const button = this.perks.querySelector('[data-perks-enroll]');
      if (this.checkingPerksCart || button.hasAttribute('aria-disabled') || this.perks.dataset.membership !== 'guest') return;

      const form = event.target;
      this.checkingPerksCart = true;
      button.setAttribute('aria-busy', 'true');
      this.renderMembership(this.perks.dataset.membership);
      form.handleErrorMessage?.();
      let addPerks = false;
      try {
        const response = await fetch(`${theme.routes.cart_url}.js`, { cache: 'no-store', credentials: 'same-origin' });
        if (!response.ok) throw new Error('Cart check failed');
        const cart = await response.json();
        if (!Array.isArray(cart.items)) throw new Error('Invalid cart');
        this.onCartUpdate({ cart });
        if (!this.isConnected || this.perks.dataset.membership !== 'guest') return;
        if (this.perks.dataset.perksInCart === 'true') {
          await this.perks.hide?.();
          const drawer = document.querySelector('cart-drawer');
          if (drawer) drawer.show(this.perks.activeElement);
          else window.location.href = theme.routes.cart_url;
        } else if (this.perks.dataset.perksAvailable === 'true') {
          addPerks = true;
        }
      } catch {
        form.handleErrorMessage?.(theme.cartStrings.error);
      } finally {
        this.checkingPerksCart = false;
        button.removeAttribute('aria-busy');
        this.renderMembership(this.perks.dataset.membership);
      }
      if (addPerks && this.isConnected && this.perks.dataset.membership === 'guest') {
        this.submitPerksForm = true;
        try { form.requestSubmit(button); }
        finally { this.submitPerksForm = false; }
      }
    }

    async checkMembership() {
      if (!this.perks) return;
      this.membershipRequest?.abort();
      this.verifiedMemberEmail = null;
      const email = this.perks.dataset.customerEmail?.trim();
      if (this.perks.dataset.customerLoggedIn !== 'true' || !email) return this.renderMembership('guest');
      this.renderMembership('loading');
      const controller = new AbortController();
      this.membershipRequest = controller;
      let timedOut = false;
      const timeout = setTimeout(() => { timedOut = true; controller.abort(); }, 10000);
      try {
        const url = new URL('/apps/ebbo-loyalty/api/membership-status', window.location.origin);
        url.searchParams.set('email', email);
        const response = await fetch(url, { signal: controller.signal, cache: 'no-store', credentials: 'same-origin' });
        if (!response.ok) throw new Error('Membership check failed');
        const data = await response.json();
        if (typeof data.isActive !== 'boolean') throw new Error('Invalid membership status');
        if (!controller.signal.aborted && this.isConnected) {
          if (this.perks.dataset.customerLoggedIn !== 'true') return this.renderMembership('guest');
          if (this.perks.dataset.customerEmail?.trim() !== email) return this.checkMembership();
          this.verifiedMemberEmail = data.isActive ? email : null;
          this.renderMembership(data.isActive ? 'member' : 'guest');
        }
      } catch {
        if (this.isConnected && (!controller.signal.aborted || timedOut)) this.renderMembership('error');
      } finally {
        clearTimeout(timeout);
      }
    }

    renderMembership(state) {
      const email = this.perks.dataset.customerEmail?.trim();
      if (this.perks.dataset.customerLoggedIn !== 'true' || !email) {
        this.verifiedMemberEmail = null;
        state = 'guest';
      }
      else if (state === 'member' && this.verifiedMemberEmail !== email) state = 'error';
      this.perks.dataset.membership = state;
      const member = state === 'member';
      const inCart = this.perks.dataset.perksInCart === 'true';
      const available = this.perks.dataset.perksAvailable === 'true';
      this.perks.querySelector('[data-guest-copy]').hidden = state !== 'guest';
      const memberCopy = this.perks.querySelector('[data-member-copy]');
      if (memberCopy) memberCopy.hidden = !member;
      const join = this.perks.querySelector('[data-perks-enroll]');
      join.hidden = member;
      join.disabled = state !== 'guest' || (!available && !inCart) || this.checkingPerksCart === true;
      const memberButton = this.perks.querySelector('[data-perks-member]');
      if (memberButton) memberButton.hidden = !member;
      this.perks.querySelector('[data-membership-retry]').hidden = state !== 'error';
      const status = this.perks.querySelector('[data-membership-status]');
      status.textContent = state === 'loading' ? this.config.checking : state === 'error' ? this.config.error : state === 'guest' && !available && !inCart ? this.config.unavailable : '';
    }

    headerIsMoving() {
      return [this.header, ...(this.topbars || [])].some((element) =>
        (element?.getAnimations?.({ subtree: true }) || []).some((animation) =>
          animation.playState === 'running' && /transform|translate|height|padding|margin|top|grid-template-rows/.test(animation.transitionProperty || '')
        )
      );
    }

    holdHeaderState() {
      const navigation = this.scrollNavigation;
      const header = this.header;
      if (!navigation || !header?.classList.contains('header-sticky')) return;
      const hidden = header.classList.contains('header-hidden');
      if (navigation.headerHidden === undefined) {
        if (navigation.direction > 0 && hidden) navigation.headerHidden = true;
        if (navigation.direction < 0 && !hidden) navigation.headerHidden = false;
      }
      if (navigation.headerHidden !== undefined && hidden !== navigation.headerHidden) {
        header.classList.toggle('header-hidden', navigation.headerHidden);
      }
    }

    navigationOffset() {
      const offset = Number(this.mobile.matches ? this.config.mobileOffset : this.config.desktopOffset) || 0;
      let visibleBottom = 0;
      for (const topbar of this.topbars || []) {
        const bounds = topbar.getBoundingClientRect();
        if (bounds.top < window.innerHeight) visibleBottom = Math.max(visibleBottom, bounds.bottom);
      }
      const header = this.header;
      if (header?.classList.contains('header-sticky')) {
        const movingHeader = header.querySelector('header[is="sticky-header"]') || header;
        const bounds = movingHeader.getBoundingClientRect();
        if (bounds.top < window.innerHeight) visibleBottom = Math.max(visibleBottom, bounds.bottom);
      }
      return offset + visibleBottom;
    }

    navigationTargets() {
      return Array.from(this.nav.querySelectorAll('a[href^="#"]')).map((link) => {
        let target;
        try { target = document.getElementById(decodeURIComponent(link.hash.slice(1))); } catch {}
        return { link, target };
      }).filter(({ target }) => target?.getClientRects().length);
    }

    updateNavigation() {
      if (!this.navHome?.isConnected) return;
      const home = this.navHome.getBoundingClientRect();
      const offset = this.navigationOffset();
      const mobileAlwaysPinned = this.mobile.matches && !this.hasAttribute('data-mobile-nav-inline');
      const pinned = mobileAlwaysPinned || home.top <= offset;
      this.nav.classList.toggle('is-pinned', pinned);
      this.nav.style.position = pinned ? 'fixed' : 'absolute';
      this.nav.style.top = `${pinned ? offset : home.top + window.scrollY}px`;
      this.nav.style.left = `${this.mobile.matches ? 0 : home.left + (pinned ? 0 : window.scrollX)}px`;
      this.nav.style.width = this.mobile.matches ? '100%' : `${home.width}px`;
      const navHeight = this.nav.getBoundingClientRect().height;
      this.navHome.style.height = mobileAlwaysPinned ? '0px' : `${navHeight}px`;
      this.navClearance.style.height = `${navHeight}px`;
      document.documentElement.style.setProperty('--bfcm-campaign-offset', `${offset + navHeight + 16}px`);
      const targets = this.navigationTargets();
      const position = offset + navHeight + 24;
      const passed = targets.filter(({ target }) => target.getBoundingClientRect().top <= position)
        .sort((a, b) => a.target.getBoundingClientRect().top - b.target.getBoundingClientRect().top);
      const atPageEnd = window.scrollY > 0 && window.scrollY >= document.documentElement.scrollHeight - window.innerHeight - 1;
      const lastVisible = atPageEnd && targets.at(-1)?.target.getBoundingClientRect().top < window.innerHeight ? targets.at(-1)?.link : null;
      const active = this.scrollNavigation?.link || lastVisible || passed.at(-1)?.link || targets[0]?.link || this.nav.querySelector('a[href^="#"]');
      const tracking = passed.length > 0;
      const rail = this.nav.querySelector('[data-campaign-nav-rail]');
      if (this.mobile.matches && !tracking && this.wasTracking) rail.scrollLeft = 0;
      this.nav.querySelectorAll('a').forEach((link) => {
        link.classList.toggle('is-active', link === active);
        if (link === active) link.setAttribute('aria-current', 'location');
        else link.removeAttribute('aria-current');
      });
      if (active && (this.activeLink !== active || this.wasTracking !== tracking || this.wasMobile !== this.mobile.matches)) {
        this.activeLink = active;
        if (this.mobile.matches && tracking) this.centerLink(active);
      }
      this.wasTracking = tracking;
      this.wasMobile = this.mobile.matches;
    }

    centerLink(link) {
      const rail = this.nav.querySelector('[data-campaign-nav-rail]');
      const bounds = rail.getBoundingClientRect();
      const target = link.getBoundingClientRect();
      const maxScroll = Math.max(0, rail.scrollWidth - rail.clientWidth);
      const rtl = window.getComputedStyle(rail).direction === 'rtl';
      const desired = rail.scrollLeft + target.left + target.width / 2 - bounds.left - rail.clientWidth / 2;
      const left = Math.min(rtl ? 0 : maxScroll, Math.max(rtl ? -maxScroll : 0, desired));
      if (Math.abs(left - rail.scrollLeft) < 1) return;
      rail.scrollTo({ left, behavior: this.reducedMotion.matches ? 'instant' : 'smooth' });
    }

    onNavigationClick(event) {
      const link = event.target.closest('a[href^="#"]');
      const target = this.navigationTargets().find((entry) => entry.link === link)?.target;
      if (!target) return;
      event.preventDefault();
      this.cancelNavigation();
      const startY = window.scrollY;
      const destination = this.navigationTop(target);
      const distance = Math.abs(destination - startY);
      this.scrollNavigation = {
        link, target, startY,
        direction: Math.sign(destination - startY),
        duration: Math.min(1100, Math.max(700, distance * 0.3)),
        elapsed: 0, stableFrames: 0,
      };
      this.updateNavigation();
      this.navigationFrame = requestAnimationFrame((time) => this.animateNavigation(time));
      if (this.mobile.matches) this.centerLink(link);
    }

    navigationTop(target) {
      const top = target.getBoundingClientRect().top + window.scrollY - this.navigationOffset() - this.nav.getBoundingClientRect().height - 16;
      const maxScroll = Math.max(0, document.documentElement.scrollHeight - window.innerHeight);
      return Math.min(maxScroll, Math.max(0, top));
    }

    animateNavigation(time) {
      this.navigationFrame = null;
      const navigation = this.scrollNavigation;
      if (!navigation) return;
      if (!navigation.target.isConnected) {
        this.cancelNavigation();
        this.schedule();
        return;
      }
      this.holdHeaderState();
      if (navigation.lastTime !== undefined) navigation.elapsed += Math.min(48, time - navigation.lastTime);
      navigation.lastTime = time;
      const progress = this.reducedMotion.matches ? 1 : Math.min(1, navigation.elapsed / navigation.duration);
      const eased = progress < 0.5 ? 4 * progress ** 3 : 1 - (-2 * progress + 2) ** 3 / 2;
      const top = this.navigationTop(navigation.target);
      const position = navigation.startY + (top - navigation.startY) * eased;
      if (Math.abs(position - window.scrollY) > 0.5) window.scrollTo({ top: position, behavior: 'instant' });
      const settled = progress === 1 && Math.abs(top - window.scrollY) <= 1 && !this.headerIsMoving();
      navigation.stableFrames = settled ? navigation.stableFrames + 1 : 0;
      if (navigation.stableFrames >= 3) {
        this.cancelNavigation();
        this.updateNavigation();
        return;
      }
      this.navigationFrame = requestAnimationFrame((nextTime) => this.animateNavigation(nextTime));
    }

    cancelNavigation() {
      cancelAnimationFrame(this.navigationFrame);
      this.navigationFrame = null;
      this.scrollNavigation = null;
    }
  });
})();
